import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Plus } from "lucide-react";
import type { FacilityNode } from "@arc/contracts";
import type { AuthSession } from "../../../auth/keycloak.js";
import { Button } from "@/components/ui/button";
import { useFacilities } from "../useFacilities.js";
import {
  archivedSubtreeRootIds,
  buildArchivedForest,
  buildForest,
  compareFacilityName,
  excludeArchived,
  isManageable,
  searchTree,
  type FacilityTreeNode,
  facilityPathLabel,
} from "../facility-tree.js";
import { useFacilityAuthz } from "../useFacilityAuthz.js";
import { useManualFavouritesList } from "../useManualFavouritesList.js";
import { ManualReferenceElement } from "../ManualReferenceElement.js";
import { ManualViewer } from "../manual-viewer/ManualViewer.js";
import { ManualEditor } from "../manual-editor/ManualEditor.js";
import { FacilityInlineCreate } from "../FacilityInlineCreate.js";
import { useFacilityNavParams } from "./useFacilityNavParams.js";
import { useDrilldownActions } from "./useDrilldownActions.js";
import { FacilityFilterBar, type FilterKey } from "./FacilityFilterBar.js";
import { FacilityRailList } from "./FacilityRailList.js";
import { ArchivedFacilityTree } from "./ArchivedFacilityTree.js";
import { FacilityBreadcrumb } from "./FacilityBreadcrumb.js";
import { DrillLevelHeader } from "./DrillLevelHeader.js";
import { ManualsViewShell } from "./ManualsViewShell.js";
import { FacilitySearchView } from "./FacilitySearchView.js";
import { EditModeToggle } from "./EditModeToggle.js";
import { FacilityPreviewPanel } from "./FacilityPreviewPanel.js";
import type { KebabProps } from "./useDrilldownActions.js";
import { useMediaQuery } from "@/lib/use-media-query";

/** The preview panel only shows at the legacy desktop breakpoint (Tailwind `lg`); below this the list stands alone. */
const PANEL_QUERY = "(min-width: 1081px)";
/** Below Tailwind `md`, where the filter bar hides the edit switch. */
const NARROW_QUERY = "(max-width: 767.98px)";

/**
 * The drill-down facility navigator (FN / ADR-014) — the `"drilldown"` alternative to the classic master-detail
 * `FacilitySelection`, gated per tenant in `App.tsx`. Three views, driven by URL params (`useFacilityNavParams`):
 * - **root list** (no params) — the realm's root facilities as cards on a timeline rail;
 * - **drill level** (`arc_fac_selected`) — a breadcrumb + the facility + its DIRECT children;
 * - **manuals** (`arc_facility`) — the reused `FacilityManualsView`, full-width.
 *
 * All derived client-side from the single `GET /facilities` tree (no API change): browse shows ACTIVE facilities
 * (`excludeArchived`, which hoists active descendants of archived parents); ancestors for the breadcrumb come from the
 * (hoisted) parent chain. FN2 is read-only navigation; management (kebab) + search + favorites arrive in FN3/FN4.
 */
export function FacilityDrilldown({ session }: { session: AuthSession | null }) {
  const { t } = useTranslation();
  const facilities = useFacilities(session);
  const nav = useFacilityNavParams();
  const { selectedId, manualsId, openManualId, editManualId, searchQuery, searchTab, drillTo, openManuals, openManualDetails, closeManuals, openSearch, closeSearch, setSearchTab, dropStale } = nav;

  const flat = useMemo(() => facilities.data ?? [], [facilities.data]);
  // Client authz gates (shared with classic, FN3a) — drive the per-card kebab + the add-root affordance.
  const authz = useFacilityAuthz(flat);
  const bypass = authz.bypass;
  // Facility management (kebab actions + drawers/dialogs + post-op navigation) — FN3.
  const actions = useDrilldownActions(session, flat, authz, { selectedId, drillTo });
  // The caller's favourite MANUALS as full cards (MFG) — the drill-down has no manuals loaded, so the global "Manual
  // favourites" chip + list come from a dedicated endpoint (active + directly-viewable, with facility location).
  const manualFavourites = useManualFavouritesList(session);
  // The active filter chip (FN4a) — `"all"` is the drill navigation below; `favourites`/`archived` swap in a global flat
  // list. Client view-state only (not a URL param); resets to `all` whenever the user navigates OUT of a flat list.
  const [pickedFilter, setFilter] = useState<FilterKey>("all");
  // "Bereiche editieren" (redesign 2026-10-21): OFF on every load (owner decision) — pure navigation until switched on.
  // It only HIDES/SHOWS the management affordances; every action stays gated by `authz` and the server.
  const [editMode, setEditMode] = useState(false);
  // Phones get a reduced view (owner 2026-11-02): no edit switch below `md`, so edit mode can't stay on there either.
  const narrow = useMediaQuery(NARROW_QUERY);
  const editing = editMode && !narrow;
  // The Archived list only exists while editing (owner 2026-11-02).
  const filter = pickedFilter === "archived" && !editing ? "all" : pickedFilter;
  const changeEditMode = (on: boolean) => {
    setEditMode(on);
    if (!on && pickedFilter === "archived") setFilter("all");
  };
  // The card shown in the preview panel. Falls back to the level's first card whenever the picked id isn't on the
  // current level (initial load, after drilling), so there is always a selection while the panel is mounted.
  const [pickedId, setPickedId] = useState<number | null>(null);
  const wide = useMediaQuery(PANEL_QUERY);
  // Browse the ACTIVE tree (archived hidden, active descendants hoisted — same as classic All/Favorites).
  const activeFlat = useMemo(() => excludeArchived(flat), [flat]);
  const roots = useMemo(() => buildForest(activeFlat), [activeFlat]);
  const byId = useMemo(() => {
    const m = new Map<number, FacilityTreeNode>();
    const walk = (ns: readonly FacilityTreeNode[]): void => {
      for (const n of ns) {
        m.set(n.id, n);
        walk(n.children);
      }
    };
    walk(roots);
    return m;
  }, [roots]);

  // The FULL tree (archived INCLUDED) — the flat lists (Favourites/Archived, FN4a) resolve nodes + their ancestor paths
  // here, so an archived facility or an archived ancestor still renders with counts + a path. `byId` (active-only) stays
  // the source of truth for drill navigation; these are read-only for the flat views.
  const allForest = useMemo(() => buildForest(flat), [flat]);
  const byIdAll = useMemo(() => {
    const m = new Map<number, FacilityTreeNode>();
    const walk = (ns: readonly FacilityTreeNode[]): void => {
      for (const n of ns) {
        m.set(n.id, n);
        walk(n.children);
      }
    };
    walk(allForest);
    return m;
  }, [allForest]);
  // "Root / Parent / Child" breadcrumb for a flat-list card (empty for a root facility). Resolves over the full tree.
  const pathLabelOf = (node: FacilityTreeNode): string => facilityPathLabel(byIdAll, node.id);
  // FULL path INCLUDING the facility's own name, for a manual search hit (FN4b-2). The manual carries a `facilityId`;
  // resolve it over the full tree so the card shows "Root / … / Facility". null ⇒ the id isn't in the loaded tree (an
  // edge case, since search is permission-filtered to visible facilities) → the card falls back to the server leaf name.
  const resolveFacilityPath = (facilityId: number): string | null => {
    const node = byIdAll.get(facilityId);
    if (!node) return null;
    const crumb = pathLabelOf(node);
    return crumb ? `${crumb} / ${node.name}` : node.name;
  };
  // Favourites list — the caller's favourite facilities resolved over the ACTIVE tree (`byId`), sorted A-Z. Resolving via
  // the active tree means an **archived** favourite is excluded here (it belongs under Archived, restore-only — never as
  // an active card with a star/kebab/dead-end nav; panel) and the counts match the active browse. Any id that doesn't
  // resolve (lost access, or archived) is defensively skipped (panel #6). The path breadcrumb uses the full-tree lookup.
  const favouriteNodes = useMemo(() => {
    const out: FacilityTreeNode[] = [];
    for (const id of actions.favorites) {
      const n = byId.get(id);
      if (n) out.push(n);
    }
    return out.sort(compareFacilityName);
  }, [actions.favorites, byId]);
  // Archived list — every archived facility realm-wide (drives the chip COUNT).
  const archivedNodes = useMemo(() => [...byIdAll.values()].filter((n) => n.archived).sort(compareFacilityName), [byIdAll]);
  // Archived FOREST — archived nodes nested under their nearest ARCHIVED ancestor (relinking over mixed archived→active→
  // archived chains, panel), so the roots ARE the archived-subtree roots. Rendered as a collapsible tree
  // ({@link ArchivedFacilityTree}) so deep descendants group under their root instead of scattering as breadcrumb cards.
  const archivedForest = useMemo(() => buildArchivedForest(flat), [flat]);
  // Search — facilities tab (FN4b): facilities whose name matches `arc_search`, over the ACTIVE tree (plan-panel #4: search
  // `activeFlat` so every hit resolves in `byId` + navigates — an archived hit would dead-end), sorted A-Z. Client-side
  // over the already-permission-filtered tree ⇒ permission-safe (no endpoint). The manual tabs are FN4b-2 (RED ZONE).
  const searchResults = useMemo(() => {
    if (!searchQuery) return [];
    const { matchIds } = searchTree(activeFlat, searchQuery);
    const out: FacilityTreeNode[] = [];
    for (const id of matchIds) {
      const n = byId.get(id);
      if (n) out.push(n);
    }
    return out.sort(compareFacilityName);
  }, [searchQuery, activeFlat, byId]);

  // Ancestor chain (root → parent, excluding self) over the hoisted browse hierarchy, for the breadcrumb.
  const ancestorsOf = (node: FacilityTreeNode): (FacilityNode | null)[] => {
    const chain: (FacilityNode | null)[] = [];
    let pid = node.parentId;
    while (pid !== null) {
      const p = byId.get(pid);
      if (!p) {
        chain.unshift(null); // an archived/absent ancestor → non-clickable placeholder
        break;
      }
      chain.unshift(p);
      pid = p.parentId;
    }
    return chain;
  };

  const manualsNode = manualsId !== null ? (byId.get(manualsId) ?? null) : null;
  const manualsValid = manualsNode !== null && manualsNode.permission !== null; // panel #1: permission-gated, not counts
  const selectedNode = selectedId !== null ? (byId.get(selectedId) ?? null) : null;
  // The global search (FN4b) — active once the tree has resolved; manuals (`arc_facility`) take precedence over search.
  const searchActive = searchQuery !== null && !manualsValid && !facilities.isPending && !facilities.isError;

  // Stale/invalid ids: once the data has RESOLVED and a param points at a missing (deleted/archived/other-realm) or
  // non-openable node, drop that param via replaceState (no new history entry). Never during load (would clobber a
  // legit deep-link still fetching).
  useEffect(() => {
    // Only drop a stale param once the tree has SUCCESSFULLY resolved — never while pending, AND never on an ERROR
    // (a failed fetch leaves `flat` empty, so a valid deep-link id reads as "missing" and would be stripped before the
    // user can retry; on "Try again" the param would already be gone → land at root instead of the linked view — panel).
    if (facilities.isPending || facilities.isError) return;
    if (manualsId !== null && !manualsValid) dropStale("arc_facility");
    if (selectedId !== null && selectedNode === null) dropStale("arc_fac_selected");
  }, [facilities.isPending, facilities.isError, manualsId, manualsValid, selectedId, selectedNode, dropStale]);

  const canManageManuals = (node: FacilityNode): boolean =>
    bypass || (node.permission !== null && node.permission !== "view");
  // The `confirm` tier (a subset of manage-manuals) — publishes/unpublishes a manual + clears a flag (ADR-018). Gates the
  // Confirm / Unmark / reopen-a-confirmed-manual review transitions so a manuals_manage-only caller isn't shown a
  // guaranteed-403 button (panel). Cascade: view < manuals_manage < confirm < manage < access_manage.
  const canConfirmManuals = (node: FacilityNode): boolean =>
    bypass || node.permission === "confirm" || node.permission === "manage" || node.permission === "access_manage";

  // The filter bar (FN4 / Mockup A) stays on EVERY facility-list level — root AND drilled-in — so filters persist while
  // navigating; hidden only in the manuals view / while loading. Counts are DISPLAY-only for now (no filtering yet).
  const showFilters = !manualsValid && !facilities.isPending && !facilities.isError;
  // The All chip + "X of Y" summary are per-LEVEL (the roots, or the drilled-into node's direct children); the
  // Favourites/Archived chips are GLOBAL (realm-wide — they're flat lists, not level concepts — fork #1).
  const levelNodes = selectedNode ? selectedNode.children : roots;
  const levelParentId = selectedNode ? selectedNode.id : null;
  const archivedAtLevel = flat.filter((f) => f.parentId === levelParentId && f.archived).length;
  const filterCounts = {
    all: levelNodes.length + archivedAtLevel, // per-level total (active shown + archived)
    shown: levelNodes.length, // per-level active count (the "X of Y" numerator)
    favourites: favouriteNodes.length, // GLOBAL, resolvable-only (panel #6)
    manual: manualFavourites.data?.length ?? 0, // GLOBAL favourite manuals (active + directly-viewable — MFG)
    archived: archivedNodes.length, // GLOBAL
  };
  // Archived-subtree ROOTS — only these get an Unarchive button; archived descendants restore with their parent (ADR-006).
  const archRootIds = useMemo(() => archivedSubtreeRootIds(flat), [flat]);

  // Edit mode — offered only to a caller who can manage SOMETHING (or an empty realm, so the first root is reachable).
  const canEdit = bypass || flat.length === 0 || flat.some((n) => isManageable(n));
  // Kebabs exist only in edit mode; outside it every card/header gets `null` (no ⋮), same as a caller who can't act.
  const kebabOf = (node: FacilityNode, opts?: { isCurrent?: boolean }): KebabProps | null =>
    editing ? actions.getKebabProps(node, opts) : null;

  // Preview panel: wide screens, the drill navigation ("all"), a non-empty level.
  const selectedCard = levelNodes.find((n) => n.id === pickedId) ?? levelNodes[0] ?? null;
  const showPanel = wide && filter === "all" && selectedCard !== null;
  const panelParentLabel = selectedNode ? selectedNode.name : t("facilitySelection.chip.all");
  // A child row in the panel: go INTO the selected facility's level and pre-select that child.
  const pickChild = (childId: number) => {
    if (!selectedCard) return;
    drillTo(selectedCard.id);
    setPickedId(childId);
  };
  // Navigating OUT of a flat list returns to the drill view: reset the filter to "all", then drill / open manuals.
  const flatDrill = (id: number) => {
    setFilter("all");
    drillTo(id);
  };
  const flatOpenManuals = (id: number) => {
    setFilter("all");
    openManuals(id);
  };
  // A manual favourite opens its facility's manuals view with THAT manual already selected (details drawer open).
  const flatOpenManualDetails = (facilityId: number, manualId: number) => {
    setFilter("all");
    openManualDetails(facilityId, manualId);
  };

  // `?arc_manual=<id>` is a clean, top-level ROUTE: the full-screen reader, rendered with PRECEDENCE over every other
  // view. Returning here (instead of layering it over the tree) means the manuals view NEVER mounts underneath — so a
  // refresh lands straight on the reader with no facility-list flash, and the URL stays a bare `?arc_manual=<id>`. The
  // reader self-fetches the body (incl. the facility breadcrumb); "Back to overview" returns to the manual's OWN facility
  // manuals list (`onExitToFacility`), or clears the param if the body never loaded (`onClose`). (`actions.portals` isn't
  // needed here — the reader owns the screen.)
  // `?arc_edit=<id>` — the full-screen AUTHORING editor (ADR-002 Phase 10), the HIGHEST-precedence top-level route (it wins
  // over the reader). Same shape as the reader: it self-fetches the body (incl. the facility breadcrumb), "Back to overview"
  // returns to the manual's OWN facility manuals list (REPLACE, so Back can't re-open the editor), and the param is cleared
  // if the body never loaded. Returned above `openManualId` so an `arc_edit` deep-link never flashes the reader underneath.
  if (editManualId !== null) {
    return (
      <ManualEditor
        key={editManualId}
        session={session}
        manualId={editManualId}
        onClose={() => dropStale("arc_edit")}
        onExitToFacility={(id) => openManuals(id, true)}
      />
    );
  }

  if (openManualId !== null) {
    // `key` by the manual id so navigating manual→manual (Back/Forward changing `arc_manual`) REMOUNTS the reader —
    // otherwise its `started`/`currentStepId`/`finished` state leaks across manuals (panel High). Exit REPLACES the
    // reader history entry (not push) so browser Back from the returned list can't re-open the reader (panel consensus).
    return (
      <ManualViewer
        key={openManualId}
        session={session}
        manualId={openManualId}
        onClose={() => dropStale("arc_manual")}
        onExitToFacility={(id) => openManuals(id, true)}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {searchActive ? (
        // Global search (FN4b — the legacy "Suche") — a top-level view (like manuals), replacing the tree. Its results
        // navigate via the nav hook's `drillTo`/`openManuals`, which clear `arc_search` (so picking a result exits search).
        <FacilitySearchView
          query={searchQuery ?? ""}
          results={searchResults}
          session={session}
          searchTab={searchTab}
          onSearchTabChange={setSearchTab}
          resolveFacilityPath={resolveFacilityPath}
          getPathLabel={pathLabelOf}
          getKebab={(node) => kebabOf(node)}
          favorites={actions.favorites}
          onToggleFavorite={actions.onToggleFavorite}
          onDrill={drillTo}
          onOpenManuals={openManuals}
          onSearch={openSearch}
          onClose={closeSearch}
        />
      ) : (
        <>
      {/* Header — same centered column + left padding as the tree below, so the title/filters align with it. HIDDEN in
          the manuals view: {@link ManualsViewShell} owns its own breadcrumb + facility title there, so the generic
          "Facility selection" bar was redundant. Shows the filter bar for root/drill, else the title (loading/error). */}
      {!manualsValid ? (
        <div className="bg-background border-card border-b pt-5 pb-4 sm:pt-7 sm:pb-5">
          <div className="page-column">
            {showFilters ? (
              <FacilityFilterBar
                counts={filterCounts}
                active={filter}
                onActiveChange={setFilter}
                onSearch={openSearch}
                showArchived={editing}
                trailing={canEdit ? <EditModeToggle isSelected={editMode} onChange={changeEditMode} /> : null}
              />
            ) : (
              <h1 id="facilities-heading" className="text-2xl font-bold tracking-tight">
                {t("facilitySelection.facilityHeader")}
              </h1>
            )}
          </div>
        </div>
      ) : null}

      {manualsValid && manualsNode ? (
        <ManualsViewShell
          facility={manualsNode}
          ancestors={ancestorsOf(manualsNode)}
          session={session}
          canManageManuals={canManageManuals(manualsNode)}
          canConfirmManuals={canConfirmManuals(manualsNode)}
          // Facility-management gate for the Area-info drawer's Manage mode — manage/access_manage/bypass, distinct
          // from the wider `canManageManuals` (ADR-016 panel #1).
          canManageFacility={bypass || isManageable(manualsNode)}
          onHome={() => drillTo(null)}
          onCrumb={(id) => drillTo(id)}
          onBack={closeManuals}
        />
      ) : (
        // No own overflow: the shell is `min-h-dvh` (unbounded), so the WINDOW scrolls. An `overflow-y-auto` here never
        // scrolls but still captures the preview panel's `sticky` — which then never sticks.
        <div className="bg-muted flex-1">
          <div className="page-column flex items-start gap-6 pt-4 pb-14 sm:pt-6">
            <div className="min-w-0 flex-1">
            {facilities.isPending ? (
              <p className="text-muted-foreground flex items-center gap-2 py-8 text-sm">
                <Loader2 aria-hidden="true" className="size-4 animate-spin" /> {t("facilitySelection.loading")}
              </p>
            ) : facilities.isError ? (
              <div className="text-muted-foreground flex flex-col items-start gap-2 py-8 text-sm">
                <span>{t("facilitySelection.loadError")}</span>
                <Button variant="outline" size="sm" onPress={() => void facilities.refetch()} isDisabled={facilities.isFetching}>
                  {t("actions.tryAgain")}
                </Button>
              </div>
            ) : filter === "favourites" ? (
              // Favourites (FN4a) — a GLOBAL flat list of the caller's favourite facilities, each with its path. Fully
              // navigable (drill / open manuals both exit the filter back to the drill view); the star un-favourites in place.
              <FacilityRailList
                key="favourites"
                nodes={favouriteNodes}
                ariaLabel={t("facilitySelection.favouriteFacilitiesLabel")}
                emptyLine={t("facilitySelection.favouriteFacilitiesEmpty")}
                getKebab={(node) => kebabOf(node)}
                favorites={actions.favorites}
                onToggleFavorite={actions.onToggleFavorite}
                onDrill={flatDrill}
                onOpenManuals={flatOpenManuals}
                getPathLabel={pathLabelOf}
              />
            ) : filter === "manual" ? (
              // Manual favourites (MFG) — a GLOBAL flat list of the caller's favourite manuals (active + directly-
              // viewable), each a plain card: name + facility path + a filled "open" that jumps to that facility's manuals
              // view. Nav-only for now (un-favourite from the manuals view). Loading/error/empty mirror the search view's
              // manual tab.
              manualFavourites.isPending ? (
                <p className="text-muted-foreground flex items-center gap-2 py-8 text-sm">
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" /> {t("facilitySelection.favouriteManualsLoading")}
                </p>
              ) : manualFavourites.isError ? (
                <div className="text-muted-foreground flex flex-col items-start gap-2 py-8 text-sm">
                  <span>{t("facilitySelection.favouriteManualsError")}</span>
                  <Button variant="outline" size="sm" onPress={() => void manualFavourites.refetch()} isDisabled={manualFavourites.isFetching}>
                    {t("actions.tryAgain")}
                  </Button>
                </div>
              ) : manualFavourites.data.length === 0 ? (
                <p className="text-muted-foreground py-8 text-sm">
                  {t("facilitySelection.favouriteManualsEmpty")}
                </p>
              ) : (
                <ul aria-label={t("facilitySelection.favouriteManualsLabel")} className="m-0 flex list-none flex-col gap-2.5 p-0">
                  {manualFavourites.data.map((result) => (
                    <li key={result.id}>
                      <ManualReferenceElement
                        manual={result}
                        path={resolveFacilityPath(result.facilityId) ?? result.facilityName}
                        onOpen={flatOpenManualDetails}
                      />
                    </li>
                  ))}
                </ul>
              )
            ) : filter === "archived" ? (
              // Archived (FN4a rework) — a GLOBAL restore-only COLLAPSIBLE TREE (parity with classic's Archived tab): no
              // star/kebab/nav; an Unarchive button on archived-subtree ROOTS only. Roots at the top level, expand to
              // reveal nested archived descendants (no more scattered breadcrumb cards).
              <ArchivedFacilityTree
                key="archived"
                roots={archivedForest}
                ariaLabel={t("facilitySelection.archivedLabel")}
                emptyLine={t("facilitySelection.archivedEmpty")}
                getPathLabel={pathLabelOf}
                getUnarchive={(node) =>
                  // Unarchive only on an archived-subtree ROOT the caller can MANAGE (mirror the kebab Archive gate +
                  // classic's Archived tab — `manageSubtreeIds` already folds in bypass; the server is still authoritative).
                  authz.manageSubtreeIds.has(node.id) && archRootIds.has(node.id)
                    ? () => actions.requestUnarchive(node)
                    : null
                }
              />
            ) : selectedNode ? (
              <div className="flex flex-col gap-3">
                <FacilityBreadcrumb
                  ancestors={ancestorsOf(selectedNode)}
                  current={selectedNode}
                  onHome={() => drillTo(null)}
                  onCrumb={(id) => drillTo(id)}
                />
                {/* Edit mode: "add a subfacility" for THIS level, right-aligned above the header (same gate as the
                    header kebab's Add child — node-level manage). */}
                {editing && actions.getKebabProps(selectedNode, { isCurrent: true })?.canManage && !actions.createUnder ? (
                  <div className="flex justify-end">
                    <Button
                      className="h-10 rounded-[10px] px-4 pl-3 text-sm font-semibold"
                      onPress={() => actions.getKebabProps(selectedNode, { isCurrent: true })?.onAddChild()}
                    >
                      <Plus aria-hidden="true" />
                      {t("facilitySelection.addSubfacility")}
                    </Button>
                  </div>
                ) : null}
                <DrillLevelHeader
                  node={selectedNode}
                  kebab={kebabOf(selectedNode, { isCurrent: true })}
                  isFavorite={actions.favorites.has(selectedNode.id)}
                  onToggleFavorite={actions.onToggleFavorite}
                  onReturnToParent={() => drillTo(selectedNode.parentId)}
                  onOpenManuals={openManuals}
                />
                {/* Inline "new subfacility" card (Add child / Insert-above) — mounts when the create target is THIS level.
                    Reuses the tree's create card (with the inherit-parent-categories checklist). */}
                {actions.createUnder && actions.createUnder.parentId === selectedNode.id ? (
                  <FacilityInlineCreate
                    parentId={selectedNode.id}
                    parentNode={selectedNode}
                    depth={0}
                    session={session}
                    onCreated={actions.onCreated}
                    onCancel={actions.onCancelCreate}
                  />
                ) : null}
                {/* `key` = the level → remount replays the domino cascade on every drill. */}
                <FacilityRailList
                  key={selectedNode.id}
                  nodes={selectedNode.children}
                  ariaLabel={t("facilitySelection.nav.subfacilitiesOf", { name: selectedNode.name })}
                  emptyLine={t("facilitySelection.noSubfacilities")}
                  getKebab={(node) => kebabOf(node)}
                  favorites={actions.favorites}
                  onToggleFavorite={actions.onToggleFavorite}
                  onDrill={drillTo}
                  onOpenManuals={openManuals}
                  timeline
                  selectedId={showPanel ? (selectedCard?.id ?? null) : null}
                  onSelect={showPanel ? setPickedId : undefined}
                />
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {/* Add a ROOT facility at the TOP of the tree, right-aligned (bypass tier, or an empty realm so the first
                    root is reachable) — always visible, no scroll. While creating, the inline card takes this slot. */}
                {actions.createUnder && actions.createUnder.parentId === null ? (
                  <FacilityInlineCreate
                    parentId={null}
                    parentNode={null}
                    depth={0}
                    session={session}
                    onCreated={actions.onCreated}
                    onCancel={actions.onCancelCreate}
                  />
                ) : (editing && bypass) || flat.length === 0 ? (
                  // Edit mode only (bypass tier) — except an EMPTY realm, where it is the only way to start.
                  <div className="flex justify-end">
                    <Button className="h-10 rounded-[10px] px-4 pl-3 text-sm font-semibold" onPress={actions.requestCreateRoot}>
                      <Plus aria-hidden="true" />
                      {t("facilitySelection.addFacility")}
                    </Button>
                  </div>
                ) : null}
                <FacilityRailList
                  key="root"
                  nodes={roots}
                  ariaLabel={t("facilitySelection.rootListLabel")}
                  emptyLine={t("facilitySelection.noAccess")}
                  getKebab={(node) => kebabOf(node)}
                  favorites={actions.favorites}
                  onToggleFavorite={actions.onToggleFavorite}
                  onDrill={drillTo}
                  onOpenManuals={openManuals}
                  selectedId={showPanel ? (selectedCard?.id ?? null) : null}
                  onSelect={showPanel ? setPickedId : undefined}
                />
              </div>
            )}
            </div>
            {showPanel && selectedCard ? (
              <FacilityPreviewPanel
                node={selectedCard}
                parentLabel={panelParentLabel}
                onOpenManuals={openManuals}
                onDrill={drillTo}
                onPickChild={pickChild}
              />
            ) : null}
          </div>
        </div>
      )}

        </>
      )}

      {/* Facility management drawers/dialogs + the polite aria-live region (rendered once; overlays portal themselves). */}
      {actions.portals}
    </div>
  );
}
